/**
 * SectionFinance — impuestos, costes y la fórmula de liquidación.
 *
 * Esto es CONFIGURACIÓN, no contabilidad: aquí se declara qué se descuenta y
 * en qué porcentaje; los importes de cada mes viven en Finanzas, no en Ajustes.
 *
 * Decisiones no obvias:
 *
 *   - Las monedas se muestran en solo lectura. Se eligen una vez en Propiedad
 *     y esta sección las reutiliza; poder editarlas aquí también daría dos
 *     fuentes para el mismo dato, justo lo contrario de lo que enseña el
 *     producto.
 *   - Todos los porcentajes de esta sección vienen de decisiones aprobadas
 *     (impuesto 10 %, comisiones de canal, fee directo 5 %, gestión 15 %) y
 *     llevan la marca "Aprobado". La pasarela de pago, en cambio, está sin
 *     decidir y se dice con esas palabras.
 *   - El bloque importante es "Cómo se calcula la liquidación": una resta
 *     vertical legible, sin color ni bordes fuertes. Las anotaciones de la
 *     derecha se derivan de lo configurado más arriba, así que la fórmula y
 *     los ajustes nunca pueden contradecirse. El detalle de la fórmula queda
 *     plegado en "Advanced" porque la mayoría de días no hace falta.
 */
import React from "react";
import { Coins } from "@phosphor-icons/react";
import {
  Block, Card, FieldGrid, Field, Select, ReadRow, ApprovedMark, Note, Advanced, FutureChip,
} from "@/pages/admin/pms/settings/SettingsPrimitives";
import { useSettingsV2, updateSection } from "@/lib/settingsV2Store";

/** 2.8 → "2,8" · 2000 → "2.000". La vista previa habla en español. */
const fmtNum = (n) => Number(n ?? 0).toLocaleString("es-ES", { maximumFractionDigits: 2 });

const CURRENCY_NAME = {
  EUR: "Euro",
  IDR: "Rupia indonesia",
  USD: "Dólar estadounidense",
  AUD: "Dólar australiano",
  SGD: "Dólar de Singapur",
};

const STATEMENT_DAYS = [1, 3, 5, 7, 10, 15];

export default function SectionFinance() {
  const { finance, property } = useSettingsV2();
  const set = (patch) => updateSection("finance", patch);

  const channelCosts = finance.channel_costs || [];
  const pcts = channelCosts.map((c) => c.pct);
  const channelRange = pcts.length
    ? `${fmtNum(Math.min(...pcts))} %–${fmtNum(Math.max(...pcts))} % según canal`
    : "";

  /* Anotación de cada línea de la resta, derivada de lo configurado arriba. */
  const lineHints = {
    "Impuestos": `${fmtNum(finance.tax_pct)} %`,
    "Coste de canal": channelRange,
    "Procesamiento de pago": `${fmtNum(finance.payment_processing_pct)} % + ${fmtNum(finance.payment_processing_fixed_idr)} IDR`,
    "Gastos operativos": "Importe mensual variable",
  };

  const baseOrder = finance.management_base_order || [];

  return (
    <div data-testid="section-finance">
      <Block
        title="Moneda"
        description="Con qué moneda se cobra en destino y en cuál se presentan los extractos e informes."
        testId="finance-currency"
      >
        <Card>
          <ReadRow
            label="Moneda operativa"
            hint="Con la que se paga en Lombok"
            value={`${property.currency_operational}${CURRENCY_NAME[property.currency_operational] ? ` · ${CURRENCY_NAME[property.currency_operational]}` : ""}`}
            testId="finance-currency-operational"
          />
          <ReadRow
            label="Moneda de informes"
            hint="La de extractos, informes y cierre mensual"
            value={`${property.currency_reporting}${CURRENCY_NAME[property.currency_reporting] ? ` · ${CURRENCY_NAME[property.currency_reporting]}` : ""}`}
            testId="finance-currency-reporting"
          />
          <p
            className="mt-4 text-xs text-stone-400 dark:text-stone-500 max-w-2xl"
            data-testid="finance-fx-note"
          >
            {finance.fx_note} Ambas monedas se eligen una vez en Propiedad; esta sección las
            reutiliza para que no puedan contradecirse.
          </p>
        </Card>
      </Block>

      <Block
        title="Impuestos"
        description="El impuesto que se aplica sobre el alojamiento y sobre qué importe se calcula."
        testId="finance-tax"
      >
        <Card>
          <FieldGrid>
            <Field
              label="Impuesto sobre alojamiento"
              type="number"
              value={finance.tax_pct}
              onChange={(v) => set({ tax_pct: v })}
              suffix="%"
              approved={{ baseline: 10, value: finance.tax_pct }}
              usedIn="finance"
              testId="finance-tax-pct"
            />
          </FieldGrid>
          <div className="mt-5 pt-4 border-t border-stone-100 dark:border-stone-800">
            <ReadRow
              label="Base del impuesto"
              hint="Se calcula sobre este importe, antes de cualquier otro descuento"
              value={finance.tax_base}
              testId="finance-tax-base"
            />
          </div>
        </Card>
      </Block>

      <Block
        title="Costes de canal"
        description="Lo que se queda cada canal por traer la reserva. Se descuenta antes de calcular la gestión."
        testId="finance-channel-costs"
      >
        <Card padded={false} testId="finance-channel-costs-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 dark:border-stone-800">
                  <th className="text-left font-normal text-[11px] tracking-[0.18em] uppercase text-stone-500 dark:text-stone-400 px-5 py-3">
                    Canal
                  </th>
                  <th className="text-right font-normal text-[11px] tracking-[0.18em] uppercase text-stone-500 dark:text-stone-400 px-5 py-3 whitespace-nowrap">
                    Coste
                  </th>
                  <th className="text-right font-normal text-[11px] tracking-[0.18em] uppercase text-stone-500 dark:text-stone-400 px-5 py-3 whitespace-nowrap">
                    Origen
                  </th>
                </tr>
              </thead>
              <tbody>
                {channelCosts.map((c) => (
                  <tr
                    key={c.key}
                    className="border-b border-stone-100 dark:border-stone-800 last:border-0"
                    data-testid={`finance-channel-cost-${c.key}`}
                  >
                    <td className="px-5 py-3 text-stone-700 dark:text-stone-300 whitespace-nowrap">
                      {c.label}
                    </td>
                    <td className="px-5 py-3 text-right text-stone-900 dark:text-stone-100 tabular-nums whitespace-nowrap">
                      {fmtNum(c.pct)} %
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      {c.approved ? (
                        <ApprovedMark />
                      ) : (
                        <span className="text-[10px] text-stone-400 dark:text-stone-500">
                          Supuesto de modelado
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <p className="mt-3 text-xs text-stone-400 dark:text-stone-500 max-w-2xl" data-testid="finance-channel-costs-hint">
          El coste de la web propia no va a un tercero: es el fee de reserva directa que el
          negocio se aplica a sí mismo.
        </p>
      </Block>

      <Block
        title="Procesamiento de pago"
        description="Lo que cuesta cobrar con tarjeta en los canales directos."
        testId="finance-payment"
        action={<FutureChip>Pasarela por decidir</FutureChip>}
      >
        <Note testId="finance-payment-note">{finance.payment_processing_note}</Note>
        <Card>
          <FieldGrid>
            <Field
              label="Comisión variable"
              type="number"
              value={finance.payment_processing_pct}
              onChange={(v) => set({ payment_processing_pct: v })}
              suffix="%"
              hint="Supuesto de la vista previa · pasarela sin decidir"
              testId="finance-payment-pct"
            />
            <Field
              label="Comisión fija por cobro"
              type="number"
              value={finance.payment_processing_fixed_idr}
              onChange={(v) => set({ payment_processing_fixed_idr: v })}
              suffix="IDR"
              hint="Supuesto de la vista previa · pasarela sin decidir"
              testId="finance-payment-fixed"
            />
          </FieldGrid>
        </Card>
      </Block>

      <Block
        title="Gestión y liquidación"
        description="La comisión del negocio por gestionar la unidad y cuándo recibe su extracto el propietario."
        testId="finance-management"
      >
        <Card>
          <FieldGrid>
            <Field
              label="Comisión de gestión"
              type="number"
              value={finance.management_fee_pct}
              onChange={(v) => set({ management_fee_pct: v })}
              suffix="%"
              approved={{ baseline: 15, value: finance.management_fee_pct }}
              hint="Sobre la base de gestión, no sobre el bruto"
              testId="finance-management-fee-pct"
            />
            <Select
              label="Día de envío del extracto"
              value={String(finance.owner_statement_day)}
              onChange={(v) => set({ owner_statement_day: Number(v) })}
              options={STATEMENT_DAYS.map((d) => ({
                value: String(d),
                label: `Día ${d} de cada mes`,
              }))}
              hint="Una vez cerrado el mes anterior"
              testId="finance-statement-day"
            />
          </FieldGrid>
        </Card>
      </Block>

      <Block
        title="Cómo se calcula la liquidación"
        description="La misma resta que verá el propietario en su extracto, con los valores configurados arriba."
        testId="finance-settlement"
      >
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Coins size={16} className="text-stone-400" />
            <span className="text-[11px] tracking-[0.18em] uppercase text-stone-500 dark:text-stone-400">
              De lo que paga el huésped al neto del propietario
            </span>
          </div>

          <div className="max-w-xl" data-testid="finance-settlement-calc">
            {baseOrder.map((raw, i) => {
              const isDeduction = raw.trim().startsWith("−");
              const text = isDeduction ? raw.trim().slice(1).trim() : raw.trim();
              return (
                <CalcLine
                  key={raw}
                  op={isDeduction ? "−" : ""}
                  label={text}
                  hint={lineHints[text]}
                  testId={`finance-settlement-line-${i}`}
                />
              );
            })}

            <div className="border-t border-stone-200 dark:border-stone-800 mt-2 pt-2">
              <CalcLine
                op="="
                label="Base de gestión"
                strong
                testId="finance-settlement-base"
              />
            </div>

            <CalcLine
              op="−"
              label="Comisión de gestión"
              hint={`${fmtNum(finance.management_fee_pct)} % de la base`}
              testId="finance-settlement-fee"
            />

            <div className="border-t border-stone-200 dark:border-stone-800 mt-2 pt-2">
              <CalcLine
                op="="
                label="Neto del propietario"
                strong
                testId="finance-settlement-net"
              />
            </div>
          </div>

          <p
            className="mt-5 text-xs text-stone-400 dark:text-stone-500 max-w-2xl"
            data-testid="finance-statement-note"
          >
            {finance.owner_statement_note}
          </p>

          <Advanced label="Ver la fórmula en detalle" testId="finance-settlement-advanced">
            <div className="space-y-3 max-w-2xl text-sm text-stone-600 dark:text-stone-300">
              <div className="text-stone-900 dark:text-stone-100 space-y-1.5 tabular-nums">
                <div data-testid="finance-formula-base">
                  Base de gestión = Alojamiento − Impuestos − Coste de canal − Procesamiento de
                  pago − Gastos operativos
                </div>
                <div data-testid="finance-formula-fee">
                  Comisión de gestión = Base de gestión × {fmtNum(finance.management_fee_pct)} %
                </div>
                <div data-testid="finance-formula-net">
                  Neto del propietario = Base de gestión − Comisión de gestión
                </div>
              </div>
              <p>
                La comisión se calcula sobre lo que queda después de restar impuestos, coste de
                canal, procesamiento de pago y gastos operativos; nunca sobre el importe bruto
                que paga el huésped.
              </p>
              <p>
                La resta empieza en los ingresos de alojamiento, que son también la base del
                impuesto. El procesamiento de pago solo aparece cuando el cobro pasa por nuestra
                pasarela: en las reservas de OTA cobra el canal.
              </p>
              <p>
                Las estancias que caen entre dos meses se reparten noche a noche, de modo que
                cada mes liquida exactamente las noches que le corresponden.
              </p>
              <p>
                Mientras el mes está abierto, los gastos operativos son una estimación. Al
                cerrarlo quedan fijados y el extracto del propietario se genera de esa foto.
              </p>
            </div>
          </Advanced>
        </Card>
      </Block>
    </div>
  );
}

/**
 * Una línea de la resta. El operador vive en su propia columna estrecha para
 * que los conceptos queden alineados aunque cambien de longitud.
 */
function CalcLine({ op, label, hint, strong, testId }) {
  return (
    <div className="flex items-baseline gap-3 py-1.5" data-testid={testId}>
      <span className="w-3 shrink-0 text-right text-stone-400 dark:text-stone-500 tabular-nums">
        {op}
      </span>
      <span className="flex-1 min-w-0 flex flex-wrap items-baseline justify-between gap-x-4">
        <span
          className={`text-sm ${
            strong
              ? "text-stone-900 dark:text-stone-100"
              : "text-stone-700 dark:text-stone-300"
          }`}
        >
          {label}
        </span>
        {hint && (
          <span className="text-xs text-stone-400 dark:text-stone-500 tabular-nums">{hint}</span>
        )}
      </span>
    </div>
  );
}
